/**
 * ModerationClassificationDialog Component
 *
 * Renders the classification lookup UI in one of three states:
 * 1. **Loading** — Full-screen LoadingOverlay while ContentSearch query runs
 * 2. **No Data** — Dialog with Illustration empty state (green cube image)
 * 3. **Data** — Full ClassificationResultDialog with classification data
 *
 * The loading state uses the same full-screen overlay pattern as CommunityDialog
 * and AddCommunityGroupDialog. The no-data state uses the same Illustration
 * component as DataList and other empty states across the application.
 *
 * Extracted from ModerationWorkQueuePage to keep that file under the line limit.
 */

import { ClassificationResultDialog } from '@classification-testing/components';
import type { ClassificationResult } from '@classification-testing/domain';
import { ErrorDisplay, Illustration, IllustrationLayout, IllustrationType } from '@core/components';
import type { DomainError } from '@core/domain';
import { Dialog, LoadingOverlay } from '@soteria/design-system-ve';

interface ModerationClassificationDialogProps {
  /** Whether the classification lookup is active */
  isOpen: boolean;
  /** Whether the search is currently in progress */
  isSearching: boolean;
  /** Whether the search has completed */
  searchCompleted: boolean;
  /** The classification result (null if no data) */
  classificationResult: ClassificationResult | null;
  /** Errors from classification search */
  errors: DomainError[] | null;
  /** Whether the content is safe */
  isSafe: boolean;
  /** Whether the content is blurred */
  isContentBlurred: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback to toggle content blur */
  onToggleBlur: () => void;
}

export function ModerationClassificationDialog({
  isOpen,
  isSearching,
  searchCompleted,
  classificationResult,
  errors,
  isSafe,
  isContentBlurred,
  onClose,
  onToggleBlur,
}: ModerationClassificationDialogProps) {
  if (!isOpen) return null;

  // Loading state — full-screen overlay (same pattern as CommunityDialog)
  if (isSearching) {
    return <LoadingOverlay fullScreen message="Loading classification data..." spinnerSize="large" />;
  }

  // Error state — show error with correlation ID before no-data check
  if (errors && errors.length > 0) {
    return (
      <ErrorDisplay
        isOpen
        correlationId={errors[0]?.correlationId}
        message={errors[0].message}
        size="xl"
        title="Error loading classification"
        onClose={onClose}
      />
    );
  }

  // Data state — full ClassificationResultDialog
  if (classificationResult) {
    return (
      <ClassificationResultDialog
        isOpen
        isContentBlurred={isContentBlurred}
        isSafe={isSafe}
        result={classificationResult}
        onClose={onClose}
        onToggleBlur={onToggleBlur}
      />
    );
  }

  // No data state — dialog with empty state illustration
  if (searchCompleted) {
    return (
      <Dialog isOpen cancelAction={{ label: 'Close' }} size="xl" title="Classification Result" onClose={onClose}>
        <Illustration
          description="No classification results were found for this content."
          layout={IllustrationLayout.HORIZONTAL}
          title="No data"
          type={IllustrationType.EMPTY}
        />
      </Dialog>
    );
  }

  return null;
}
